import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Baby, CheckCircle, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FamilyMember } from '../types/family.types';

interface MemberCardProps {
  member: FamilyMember;
  isSelected: boolean;
  isEligible: boolean;
  onToggle: (memberId: string) => void;
}

export const MemberCard = ({ member, isSelected, isEligible, onToggle }: MemberCardProps) => {
  const { t } = useTranslation('common');
  const handleClick = () => {
    if (isEligible) {
      onToggle(member.id);
    }
  };

  const handleCheckboxChange = () => {
    if (isEligible) {
      onToggle(member.id);
    }
  };

  return (
    <Card
      className={`transition-all ${
        !isEligible
          ? 'opacity-50 cursor-not-allowed bg-[var(--color-neutral-300)]/20'
          : isSelected
            ? 'ring-2 ring-[var(--color-primary-700)] bg-[var(--color-primary-100)] cursor-pointer'
            : 'hover:shadow-md cursor-pointer'
      }`}
      onClick={handleClick}
    >
      <CardContent className="p-3 md:p-4">
        <div className="relative">
          {member.isPrimary && (
            <div className="absolute -top-2 -right-2 md:right-0">
              <Badge variant="default" className="text-xs">
                <CheckCircle className="h-3 w-3 md:mr-1 hidden xs:inline" />
                <span className="hidden xs:inline">{t('events.attendees.primary')}</span>
                <span className="xs:hidden">{t('events.attendees.primary')}</span>
              </Badge>
            </div>
          )}
          <div className={`flex items-start space-x-2 md:space-x-3 ${member.isPrimary ? 'pt-5 md:pt-6' : ''}`}>
            <Checkbox
              checked={isSelected}
              disabled={!isEligible}
              onCheckedChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 h-3 w-3 md:h-4 md:w-4"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm md:text-base truncate text-[var(--color-neutral-900)]">
                {member.firstName} {member.lastName}
              </h4>
              {member.email && (
                <p className="text-xs md:text-sm text-[var(--color-neutral-700)] truncate hidden md:block">
                  {member.email}
                </p>
              )}
              {member.age && (
                <p className="text-xs md:text-sm text-[var(--color-neutral-700)]">
                  {t('events.attendees.ageBadge', { age: member.age })}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <Badge variant={member.type === 'adult' ? 'secondary' : 'default'} className="ml-auto text-xs">
                {member.type === 'adult' ? (
                  <>
                    <User className="h-3 w-3 mr-0.5 md:mr-1" />
                    <span className="hidden xs:inline">{t('events.attendees.adultBadge')}</span>
                    <span className="xs:hidden">{t('events.attendees.adultBadge')}</span>
                  </>
                ) : (
                  <>
                    <Baby className="h-3 w-3 mr-0.5 md:mr-1" />
                    <span className="hidden xs:inline">{t('events.attendees.childBadge')}</span>
                    <span className="xs:hidden">{t('events.attendees.childBadge')}</span>
                  </>
                )}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
